import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@listening/frontend/components/radix/button';
import { cn } from '@listening/utils/react-helpers';
import { findActiveWordIdx } from '@listening/frontend/audio-chunk-player/alignment';
import * as m from '@/paraglide/messages';
import { voicePortrait } from '../voice-portrait';
import { ChapterRowContent } from './chapter-row';
import { demoClip, type DemoClip } from './demo-audio-manifest';
import {
  DEMO_VOICE_KEYS,
  demoWords,
  RESEARCH_BODY_1,
  RESEARCH_BODY_2,
  RESEARCH_CITATION,
  type DemoVariant,
  type DemoVoiceKey,
} from './demo-script';

const SPEEDS = [0.75, 1, 1.25, 1.5, 2, 3];
const formatSpeed = (x: number) =>
  `${Number.isInteger(x) ? `${x.toString()}.0` : x.toString()}×`;

const SECTIONS = () => [
  { name: m.read_along_abstract(), time: '0:00' },
  { name: m.funnel_demo_sec_introduction(), time: '1:12' },
  { name: m.funnel_demo_sec_methods(), time: '4:30' },
  { name: m.funnel_demo_sec_results(), time: '8:05' },
  { name: m.funnel_demo_sec_discussion(), time: '10:40' },
];
const SECTIONS_TOTAL = '12:03';
const toSeconds = (t: string) => {
  const [minutes = 0, seconds = 0] = t.split(':').map(Number);
  return minutes * 60 + seconds;
};

export function ReaderDemo({
  variant = 'research',
  initialVoice = 'Scarlett',
  onPlay,
}: ReaderDemoProps) {
  return (
    <ReaderDemoLayout
      initialVoice={initialVoice}
      layout='full'
      onPlay={onPlay}
      variant={variant}
    />
  );
}

export function OnboardingReaderDemo({
  variant = 'research',
  initialVoice = 'Scarlett',
  onPlay,
}: ReaderDemoProps) {
  return (
    <ReaderDemoLayout
      initialVoice={initialVoice}
      layout='onboarding'
      onPlay={onPlay}
      variant={variant}
    />
  );
}

type ReaderDemoProps = {
  variant?: DemoVariant;
  initialVoice?: DemoVoiceKey;
  onPlay?: () => void;
};

function ReaderDemoLayout({
  variant,
  layout,
  initialVoice,
  onPlay,
}: Required<Pick<ReaderDemoProps, 'variant' | 'initialVoice'>> &
  Pick<ReaderDemoProps, 'onPlay'> & {
    layout: 'full' | 'onboarding';
  }) {
  const onboarding = layout === 'onboarding';
  const [voiceKey, setVoiceKey] = useState<DemoVoiceKey>(initialVoice);
  const [speedIdx, setSpeedIdx] = useState(1);
  const [playing, setPlaying] = useState(false);
  const [wordIdx, setWordIdx] = useState(-1);
  const [secIdx, setSecIdx] = useState(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const frameRef = useRef<number | null>(null);
  const playTokenRef = useRef(0);

  const words = useMemo(() => demoWords(variant), [variant]);

  const stopTracking = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    playTokenRef.current++;
    stopTracking();
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setPlaying(false);
    setWordIdx(-1);
  }, [stopTracking]);

  useEffect(() => stop, [stop]);

  const playClip = useCallback(
    (clip: DemoClip) => {
      const audio = audioRef.current;
      if (!audio) return;
      onPlay?.();
      stopTracking();
      const token = ++playTokenRef.current;
      if (audio.src.endsWith(clip.src)) audio.currentTime = 0;
      else audio.src = clip.src;
      audio.playbackRate = SPEEDS[speedIdx] ?? 1;
      void audio.play().then(
        () => {
          if (playTokenRef.current !== token) return;
          setPlaying(true);
          const step = () => {
            const el = audioRef.current;
            if (!el) return;
            setWordIdx(findActiveWordIdx(clip.words, el.currentTime * 1000));
            frameRef.current = requestAnimationFrame(step);
          };
          frameRef.current = requestAnimationFrame(step);
        },
        () => {
          if (playTokenRef.current !== token) return;
          stop();
        },
      );
    },
    [onPlay, speedIdx, stopTracking, stop],
  );

  function togglePlay() {
    if (playing) stop();
    else playClip(demoClip(variant, voiceKey));
  }
  function selectVoice(key: DemoVoiceKey) {
    setVoiceKey(key);
    playClip(demoClip(variant, key));
  }
  function cycleVoice() {
    selectVoice(
      DEMO_VOICE_KEYS[
        (DEMO_VOICE_KEYS.indexOf(voiceKey) + 1) % DEMO_VOICE_KEYS.length
      ] ?? 'Scarlett',
    );
  }
  function cycleSpeed() {
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    const audio = audioRef.current;
    if (audio) audio.playbackRate = SPEEDS[next] ?? 1;
  }

  const sections = SECTIONS();
  const scrubPct = Math.round(
    (toSeconds(sections[secIdx]?.time ?? '0:00') / toSeconds(SECTIONS_TOTAL)) *
      100,
  );
  const body1Count = RESEARCH_BODY_1.split(' ').length;

  const renderWord = (word: string, index: number) => (
    <span
      className={cn(
        'rounded-[3px] px-px transition-colors duration-100',
        index === wordIdx && 'bg-azure-595 text-white',
      )}
      key={index}
    >
      {word}
    </span>
  );

  return (
    <div className='w-full'>
      {/* oxlint-disable-next-line jsx-a11y/media-has-caption -- the clip's full
          transcript is the rendered text below, word-synchronized to playback;
          a <track> would duplicate content already in the DOM. */}
      <audio onEnded={stop} preload='none' ref={audioRef} />
      {!onboarding && (
        <div className='mb-3 flex items-center gap-1.5 text-xs font-bold tracking-wide text-azure-455'>
          ▶ {m.funnel_demo_eyebrow()}
        </div>
      )}
      <div
        className={cn(
          'flex flex-wrap items-start gap-4',
          onboarding && 'block',
        )}
      >
        {!onboarding && (
          <div className='order-2 hidden w-[162px] shrink-0 rounded-xl border border-steel-120 bg-white p-3.5 shadow-md md:block'>
            <div className='px-2 pb-2 text-[10.5px] font-bold tracking-wider text-steel-700 uppercase'>
              {m.funnel_demo_chapters()}
            </div>
            <div className='flex flex-col gap-px'>
              {sections.map((section, index) => {
                const status =
                  index < secIdx ? 'done' : index === secIdx ? 'now' : 'up';
                return (
                  <Button
                    className={cn(
                      'h-auto w-full justify-start gap-2 rounded-lg px-2 py-1.5 text-left font-normal hover:bg-mist-165',
                      status === 'now' && 'bg-mist-385',
                    )}
                    key={section.name}
                    onClick={() => setSecIdx(index)}
                    type='button'
                    variant='ghost'
                  >
                    <ChapterRowContent
                      meta={
                        status === 'done'
                          ? m.funnel_demo_sec_done()
                          : section.time
                      }
                      name={section.name}
                      status={status}
                    />
                  </Button>
                );
              })}
            </div>
            <div className='mt-1.5 border-t border-steel-120 px-2 pt-2 text-[11px] leading-snug text-steel-700'>
              {m.funnel_listen_any_section()}
            </div>
          </div>
        )}

        <div
          className={cn(
            'min-w-0 flex-1 sm:min-w-[280px]',
            !onboarding &&
              'rounded-xl border border-steel-120 bg-white p-[18px] shadow-md',
          )}
        >
          {!onboarding && (
            <div className='mb-3 flex items-center justify-between'>
              <span className='text-[15px] font-extrabold'>
                {variant === 'general'
                  ? m.funnel_demo_article_title()
                  : m.funnel_demo_paper_title()}
              </span>
              <span className='text-xs text-steel-700'>
                {variant === 'general'
                  ? m.funnel_demo_article_meta()
                  : m.funnel_demo_paper_meta()}
              </span>
            </div>
          )}

          <div className='rounded-[10px] border border-steel-120 bg-mist-330 px-[18px] py-4 text-base leading-relaxed'>
            {variant === 'general' ? (
              <>
                {words.map((word, index) => (
                  <span key={index}>{renderWord(word, index)} </span>
                ))}
              </>
            ) : (
              <>
                {RESEARCH_BODY_1.split(' ').map((word, index) => (
                  <span key={index}>{renderWord(word, index)} </span>
                ))}
                <span className='text-steel-260 line-through decoration-scarlet-400 decoration-[1.5px]'>
                  {RESEARCH_CITATION}
                </span>{' '}
                {RESEARCH_BODY_2.split(' ').map((word, index) => (
                  <span key={index + body1Count}>
                    {renderWord(word, index + body1Count)}{' '}
                  </span>
                ))}
                <span className='mt-2.5 block border-t border-dashed border-steel-120 pt-2.5 font-sans text-[13px] text-steel-260'>
                  <span className='mr-1.5 inline-block rounded-[5px] bg-scarlet-200 px-1.5 py-px text-[10px] font-bold text-scarlet-400 no-underline'>
                    {m.funnel_demo_skipped_tag()}
                  </span>
                  {m.funnel_demo_footnote()}
                </span>
              </>
            )}
          </div>

          <div className='mt-3 mb-1 flex items-center gap-1.5 px-0.5 text-[12.5px] font-semibold text-jade-750'>
            ✓{' '}
            {variant === 'general'
              ? m.funnel_demo_skip_general()
              : m.funnel_skips_citations_footnotes()}
          </div>

          <div className='mt-3 border-t border-steel-120 pt-3.5'>
            <div className='flex items-center gap-2.5'>
              <span className='text-[11.5px] text-steel-700 tabular-nums'>
                {sections[secIdx]?.time ?? '0:00'}
              </span>
              <div className='h-[5px] flex-1 overflow-hidden rounded-full bg-steel-120'>
                <i
                  className='block h-full rounded-full bg-azure-595'
                  style={{ width: `${scrubPct.toString()}%` }}
                />
              </div>
              <span className='text-[11.5px] text-steel-700 tabular-nums'>
                {SECTIONS_TOTAL}
              </span>
            </div>
            <div className='mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 sm:justify-between'>
              <div className='flex items-center gap-3'>
                <Button
                  aria-label={m.common_previous_section()}
                  className='size-8 rounded-full text-steel-880 hover:bg-mist-165'
                  onClick={() => setSecIdx((s) => Math.max(0, s - 1))}
                  size='icon'
                  type='button'
                  variant='ghost'
                >
                  ⏮
                </Button>
                <Button
                  aria-label={playing ? m.common_pause() : m.common_play()}
                  className='size-[46px] rounded-full bg-azure-595 text-white shadow-[0_6px_16px_rgba(5,135,251,0.35)] hover:bg-azure-700'
                  onClick={togglePlay}
                  size='icon'
                  type='button'
                >
                  {playing ? (
                    <span className='block size-3 rounded-[2px] bg-white' />
                  ) : (
                    <span className='ml-1 block border-y-[9px] border-l-[14px] border-y-transparent border-l-white' />
                  )}
                </Button>
                <Button
                  aria-label={m.common_next_section()}
                  className='size-8 rounded-full text-steel-880 hover:bg-mist-165'
                  onClick={() =>
                    setSecIdx((s) => Math.min(sections.length - 1, s + 1))
                  }
                  size='icon'
                  type='button'
                  variant='ghost'
                >
                  ⏭
                </Button>
              </div>
              <div className='flex items-center gap-2.5'>
                <Button
                  aria-label={m.playbar_speed()}
                  className='h-auto rounded-full bg-mist-385 px-3 py-1.5 text-[12.5px] font-bold text-azure-455 tabular-nums hover:bg-mist-605'
                  onClick={cycleSpeed}
                  type='button'
                  variant='secondary'
                >
                  {formatSpeed(SPEEDS[speedIdx] ?? 1)}
                </Button>
                <Button
                  aria-label={m.common_change_voice()}
                  className='size-[34px] rounded-full p-0'
                  onClick={cycleVoice}
                  size='icon'
                  type='button'
                  variant='ghost'
                >
                  <img
                    alt={voiceKey}
                    className='block size-[34px] rounded-full border-2 border-azure-595'
                    src={voicePortrait(voiceKey)}
                  />
                </Button>
              </div>
            </div>
          </div>

          <div className='mt-3 flex flex-wrap items-center gap-3 border-t border-steel-120 pt-3'>
            <span className='text-[11px] font-bold tracking-wider text-steel-700 uppercase'>
              {m.playbar_voice()}
            </span>
            <div className='flex flex-wrap gap-2'>
              {DEMO_VOICE_KEYS.map((key) => (
                <Button
                  className={cn(
                    'h-auto gap-2 rounded-full border-[1.5px] py-1 pr-3.5 pl-1 text-[13.5px] font-semibold',
                    key === voiceKey
                      ? 'border-azure-595 bg-mist-385'
                      : 'border-steel-120 bg-white hover:border-cloud-50',
                  )}
                  key={key}
                  onClick={() => selectVoice(key)}
                  type='button'
                  variant='outline'
                >
                  <img
                    alt=''
                    className='block size-7 rounded-full'
                    src={voicePortrait(key)}
                  />
                  <span>{key}</span>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
